import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, interval } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import * as moment from 'moment';
import * as L from 'leaflet';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';

import { GetProfileAction, SetProfileAction, UpdateProfileAction } from 'src/app/user/store/user.actions';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';
import { UserService } from '../../../user/service/user.abstract.service';
import { ProfileResponse } from '../../../user/user.model';
import { PermitCategoryService } from '../../services/permit-category.service';
import { I18nString } from '../../models/permit-category.model';
import { PermitV2Service } from '../../services/permits.abstract.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { AdminPermitListItem } from '../../models/permit.model';
import { AdminPermitFilters, SavedPermitFilter, PermitFilterProfileEntry } from '../../models/permit-filters.model';
import { SyncStatus } from '../../constants/permit.constants';
import { PermitFilterSidenavComponent } from '../permit-filter-sidenav/permit-filter-sidenav.component';
import { State } from '../../../store';
import { organisationSelector } from '../../../global/store/global.selectors';
import { Organisation } from '../../../global/global.model';
import { CreatePermitDialogComponent } from '../create-permit-dialog/create-permit-dialog.component';

@Component({
    selector: 'app-permit-list-page',
    templateUrl: './permit-list-page.component.html',
    styleUrls: ['./permit-list-page.component.scss'],
})
export class PermitListPageComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    @ViewChild(PermitFilterSidenavComponent) filterSidenav: PermitFilterSidenavComponent;

    searchControl = new FormControl('');

    filters: AdminPermitFilters = {};

    savedFilters: SavedPermitFilter[] = [];

    private currentProfileFilters: PermitFilterProfileEntry[] = [];

    private currentOrg: string;

    private skipNextProfileReload = false;

    permits: AdminPermitListItem[] = [];

    // Applicant profiles resolved by user id — the list payload denormalizes applicant name/picture
    // (often empty for self-applicants), so we resolve the real profile from `applicant.user`.
    // Deduped and cached across pages/refreshes; `fetchedApplicantIds` guards against refetching.
    private applicantProfiles = new Map<string, ProfileResponse>();

    private fetchedApplicantIds = new Set<string>();

    // Category icon slug (icon-font class) by category id — the list payload doesn't reliably carry
    // the category icon, so we resolve it from the (small) category list once on init.
    private categoryIcons = new Map<string, string>();

    total = 0;

    pageIndex = 0;

    pageSize = 10;

    pageSizeOptions = [10, 25, 50];

    sort = 'createdAt';

    sortOrder: 'asc' | 'desc' = 'desc';

    loading = false;

    activeView: 'list' | 'map' = 'list';

    showMarkerText = true;

    private map: L.Map;

    private markers: L.Marker[] = [];

    private mapFitDone = false;

    private readonly defaultLocation: [number, number] = [45.5017, -73.5673];

    private readonly defaultZoom = 13;

    private readonly zoomThreshold = 14;

    columnHeaders = [
        {
            field: 'type',
            translationKey: 'permit.manage.list.col_type',
            sortable: true,
            sortField: 'permitType',
        },
        {
            field: 'category',
            translationKey: 'permit.manage.list.col_category',
            sortable: true,
            sortField: 'category',
        },
        {
            field: 'permitId',
            translationKey: 'permit.manage.list.col_permit_id',
            sortable: true,
            sortField: 'permitId',
        },
        {
            field: 'location',
            translationKey: 'permit.manage.list.col_location',
            sortable: false,
        },
        {
            field: 'createdAt',
            translationKey: 'permit.manage.list.col_date',
            sortable: true,
            sortField: 'createdAt',
        },
        {
            field: 'status',
            translationKey: 'permit.manage.list.col_status',
            sortable: true,
            sortField: 'state',
        },
        {
            field: 'department',
            translationKey: 'permit.manage.list.col_department',
            sortable: true,
            sortField: 'department',
        },
        {
            field: 'tags',
            translationKey: 'permit.manage.list.col_tags',
            sortable: false,
        },
        {
            field: 'applicant',
            translationKey: 'permit.manage.list.col_applicant',
            sortable: false,
        },
    ];

    readonly syncColorMap: Record<SyncStatus, string> = {
        [SyncStatus.SYNCED]: '#4caf50',
        [SyncStatus.SYNC_PENDING]: '#ff9800',
        [SyncStatus.SYNC_ERROR]: '#f44336',
    };

    readonly syncTooltipMap: Record<SyncStatus, string> = {
        [SyncStatus.SYNCED]: 'permit.manage.list.sync_synced',
        [SyncStatus.SYNC_PENDING]: 'permit.manage.list.sync_pending',
        [SyncStatus.SYNC_ERROR]: 'permit.manage.list.sync_error',
    };

    private readonly REFRESH_INTERVAL_MS = 2.5 * 60 * 1000;

    readonly DEPARTMENT_DEFAULT_COLOR = '#3F51B5';

    constructor(
        private permitService: PermitV2Service,
        private messageService: MessageService,
        private router: Router,
        private route: ActivatedRoute,
        private store: Store<State>,
        private actions$: Actions,
        private translate: TranslateService,
        private userService: UserService,
        private categoryService: PermitCategoryService,
        private dialog: MatDialog,
    ) {}

    /** Populated refs/statuses carry a localized name object ({ fr, en }); resolve it to the current language. */
    localizedName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    ngOnInit(): void {
        this.store
            .select(organisationSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((organisation: Organisation) => {
                this.currentOrg = organisation?.tag;
            });

        this.loadCategoryIcons();

        this.store.dispatch(GetProfileAction());

        this.actions$.pipe(ofType(SetProfileAction), takeUntil(this.destroy$)).subscribe(({ profile }) => {
            const allFilters: PermitFilterProfileEntry[] = profile?.preferences?.permitFilters ?? [];
            this.currentProfileFilters = allFilters;
            const orgEntry = allFilters.find((item) => item.organisation === this.currentOrg);

            // Load saved filters (always update from profile)
            this.savedFilters = orgEntry?.savedFilters ?? [];

            // Skip full reload when this was triggered by our own save/delete
            if (this.skipNextProfileReload) {
                this.skipNextProfileReload = false;
                return;
            }

            if (orgEntry?.lastUsed) {
                this.filters = { ...this.filters, ...orgEntry.lastUsed };
                this.searchControl.setValue(this.filters.search || '', { emitEvent: false });
                if (this.filterSidenav) {
                    this.filterSidenav.patchFormWithFilters(orgEntry.lastUsed);
                }
            }

            this.loadPermits();
        });

        this.setupSearchListener();
        this.setupAutoRefresh();
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadPermits(): void {
        this.loading = true;
        this.permitService
            .getPermits({
                ...this.filters,
                page: this.pageIndex + 1,
                limit: this.pageSize,
                sort: this.sort,
                sortOrder: this.sortOrder,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.permits = result.data;
                    this.total = result.total;
                    this.loading = false;
                    this.resolveApplicantAvatars();
                    if (this.activeView === 'map' && this.map) {
                        this.updateMapMarkers();
                    }
                },
                error: () => {
                    this.loading = false;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    onFilterChanged(partial: Partial<AdminPermitFilters>): void {
        const preservedSearch = this.filters.search;
        this.filters = { ...partial };
        if (preservedSearch) {
            this.filters.search = preservedSearch;
        }
        this.pageIndex = 0;
        this.loadPermits();
    }

    onSaveFilter(event: { name: string; filters: AdminPermitFilters }): void {
        const newFilter: SavedPermitFilter = {
            id: Math.random().toString(36).slice(2) + Date.now().toString(36),
            name: event.name,
            filters: event.filters,
        };
        this.savedFilters = [...this.savedFilters, newFilter];
        this.persistFiltersToProfile();
    }

    onDeleteFilter(filterId: string): void {
        this.savedFilters = this.savedFilters.filter((sf) => sf.id !== filterId);
        this.persistFiltersToProfile();
    }

    onLoadFilter(savedFilter: SavedPermitFilter): void {
        this.onFilterChanged(savedFilter.filters);
        if (this.filterSidenav) {
            this.filterSidenav.patchFormWithFilters(savedFilter.filters);
        }
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.loadPermits();
    }

    onSortHeader(sortField: string): void {
        if (this.sort === sortField) {
            this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
        } else {
            this.sort = sortField;
            this.sortOrder = 'asc';
        }
        this.pageIndex = 0;
        this.loadPermits();
    }

    openPermitDetail(permit: AdminPermitListItem): void {
        this.router.navigate(['details', permit._id], { relativeTo: this.route });
    }

    /** Counter flow: the agent files a permit application on behalf of a citizen. */
    openCreatePermitDialog(): void {
        this.dialog
            .open(CreatePermitDialogComponent, {
                width: '80vw',
                height: '90vh',
                panelClass: 'fullscreen-dialog',
                autoFocus: false,
            })
            .afterClosed()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result?.refresh) {
                    this.loadPermits();
                }
            });
    }

    getLocationText(permit: AdminPermitListItem): string {
        if (!permit.location) return '';
        return permit.location.address || permit.location.siteLocation || '';
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return '';
        return moment(dateStr).format('LL');
    }

    getDepartmentChipColor(opacity = 0.15): string {
        const hex = this.DEPARTMENT_DEFAULT_COLOR.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    getSortIcon(header: any): string {
        if (!header.sortable) return '';
        if (this.sort !== header.sortField) return 'unfold_more';
        if (this.sortOrder === 'asc') return 'arrow_drop_up';
        if (this.sortOrder === 'desc') return 'arrow_drop_down';
        return 'unfold_more';
    }

    clearSearch(): void {
        this.searchControl.setValue('');
    }

    isDetailOpen(): boolean {
        return this.router.url.includes('/details/');
    }

    getInitials(firstName: string, lastName: string): string {
        const first = (firstName || '').charAt(0).toUpperCase();
        const last = (lastName || '').charAt(0).toUpperCase();
        return first + last || '?';
    }

    /** Fetch (once) the profile behind each applicant's user id so we can show a real avatar/name. */
    private resolveApplicantAvatars(): void {
        const ids = Array.from(new Set(this.permits.map((p) => p.applicant?.user).filter((id) => !!id && !this.fetchedApplicantIds.has(id)))) as string[];

        ids.forEach((id) => {
            this.fetchedApplicantIds.add(id);
            this.userService
                .getProfileById(id)
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe({
                    next: (profile) => {
                        if (profile) this.applicantProfiles.set(id, profile);
                    },
                    // Leave unresolved on error; embedded fields / initials remain the fallback.
                    error: () => undefined,
                });
        });
    }

    /** Resolve category icon slugs once; the category set is small and rarely changes. */
    private loadCategoryIcons(): void {
        this.categoryService
            .getCategories()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (categories) => {
                    (categories || []).forEach((c) => {
                        if (c.icon) this.categoryIcons.set(c._id, c.icon);
                    });
                },
                error: () => undefined,
            });
    }

    /** Icon-font class for a permit's category, falling back to any payload icon then a default. */
    categoryIcon(permit: AdminPermitListItem): string {
        const category = permit.category as { _id?: string; icon?: string } | undefined;
        const id = category?._id;
        return (id && this.categoryIcons.get(id)) || category?.icon || 'icon-folder';
    }

    private resolvedApplicant(permit: AdminPermitListItem): ProfileResponse | null {
        const id = permit.applicant?.user;
        return (id && this.applicantProfiles.get(id)) || null;
    }

    applicantPicture(permit: AdminPermitListItem): string | null {
        return this.resolvedApplicant(permit)?.picture || null;
    }

    applicantName(permit: AdminPermitListItem): string {
        const profile = this.resolvedApplicant(permit);
        const first = profile?.firstName || permit.applicant?.firstName || '';
        const last = profile?.lastName || permit.applicant?.lastName || '';
        return `${first} ${last}`.trim();
    }

    applicantInitials(permit: AdminPermitListItem): string {
        const profile = this.resolvedApplicant(permit);
        const first = profile?.firstName || permit.applicant?.firstName || '';
        const last = profile?.lastName || permit.applicant?.lastName || '';
        return this.getInitials(first, last);
    }

    toggleView(view: 'list' | 'map'): void {
        this.activeView = view;
        if (view === 'map') {
            setTimeout(() => {
                this.initializeMap();
            }, 100);
        }
    }

    getMappablePermits(): AdminPermitListItem[] {
        return this.permits.filter((permit) => {
            const coords = permit.location && permit.location.coordinates;
            return !!coords && typeof coords.lat === 'number' && typeof coords.lng === 'number' && !isNaN(coords.lat) && !isNaN(coords.lng);
        });
    }

    getMarkerColors(permit: AdminPermitListItem): { fill: string; stroke: string } {
        const color = (permit.state && permit.state.color) || '#333333';
        return { fill: this.getMarkerBackgroundColor(color), stroke: color };
    }

    getMarkerBackgroundColor(color: string): string {
        if (!color) return '#f5f5f5';

        const hex = color.replace('#', '');
        if (hex.length !== 6) return '#f5f5f5';

        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);

        const lightenFactor = 0.8;
        r = Math.round(r + (255 - r) * lightenFactor);
        g = Math.round(g + (255 - g) * lightenFactor);
        b = Math.round(b + (255 - b) * lightenFactor);

        return `rgb(${r}, ${g}, ${b})`;
    }

    buildMarkerLabel(permit: AdminPermitListItem): { typeName: string; permitId: string } {
        return {
            typeName: this.localizedName(permit.permitType && permit.permitType.name),
            permitId: permit.permitId || '',
        };
    }

    private persistFiltersToProfile(): void {
        const allFilters = [...this.currentProfileFilters];
        const orgIndex = allFilters.findIndex((item) => item.organisation === this.currentOrg);

        const orgEntry: PermitFilterProfileEntry = {
            organisation: this.currentOrg,
            lastUsed: {
                status: this.filters.status,
                category: this.filters.category,
                permitType: this.filters.permitType,
                department: this.filters.department,
                assignedTo: this.filters.assignedTo,
                dateFrom: this.filters.dateFrom,
                dateTo: this.filters.dateTo,
                syncStatus: this.filters.syncStatus,
                tags: this.filters.tags,
                search: this.filters.search,
            },
            savedFilters: this.savedFilters,
        };

        if (orgIndex >= 0) {
            allFilters[orgIndex] = orgEntry;
        } else {
            allFilters.push(orgEntry);
        }

        this.currentProfileFilters = allFilters;
        this.skipNextProfileReload = true;
        this.store.dispatch(
            UpdateProfileAction({
                payload: { preferences: { permitFilters: allFilters } },
            }),
        );
    }

    private initializeMap(): void {
        if (this.map) {
            this.map.remove();
        }
        this.mapFitDone = false;

        const mapElement = document.getElementById('permit-list-map');
        if (!mapElement) return;

        this.map = L.map('permit-list-map', {
            zoomControl: false,
        }).setView(this.defaultLocation, this.defaultZoom);

        L.control
            .zoom({
                position: 'topright',
            })
            .addTo(this.map);

        L.tileLayer(MAP_TILE_URL, {
            maxZoom: 18,
            minZoom: 13,
            attribution: MAP_TILE_ATTRIBUTION,
        }).addTo(this.map);

        this.showMarkerText = this.map.getZoom() >= this.zoomThreshold;

        this.map.on('zoomend', () => {
            const newShowMarkerText = this.map.getZoom() >= this.zoomThreshold;
            if (newShowMarkerText !== this.showMarkerText) {
                this.showMarkerText = newShowMarkerText;
                this.updateMarkerIcons();
            }
        });

        this.updateMapMarkers();
    }

    private updateMapMarkers(): void {
        if (!this.map) return;

        const currentCenter = this.map.getCenter();
        const currentZoom = this.map.getZoom();

        this.markers.forEach((marker) => marker.remove());
        this.markers = [];

        this.getMappablePermits().forEach((permit) => {
            const coords = permit.location.coordinates;
            const marker = L.marker([coords.lat, coords.lng], {
                icon: this.createMarkerIcon(permit),
            }).addTo(this.map);

            marker.on('click', () => {
                this.openPermitDetail(permit);
            });

            this.markers.push(marker);
        });

        if (this.markers.length > 0 && !this.mapFitDone) {
            const group = L.featureGroup(this.markers);
            this.map.fitBounds(group.getBounds().pad(0.1));
            this.mapFitDone = true;
        } else {
            this.map.setView(currentCenter, currentZoom);
        }
    }

    private updateMarkerIcons(): void {
        if (!this.map) return;

        this.getMappablePermits().forEach((permit, index) => {
            if (index < this.markers.length) {
                this.markers[index].setIcon(this.createMarkerIcon(permit));
            }
        });
    }

    private createMarkerIcon(permit: AdminPermitListItem): L.DivIcon {
        const colors = this.getMarkerColors(permit);
        const label = this.buildMarkerLabel(permit);

        const markerHtml = `
        <div class="pin-container">
            <div class="marker-content">
                <div class="svg-container">
                    <svg viewBox="0 0 26 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="Vector" filter="url(#filter0_d_306_3188)">
                            <path d="M12.9543 1C17.505 1 20.9086 5.01135 20.9086 10.1343C20.9086 14.7536 16.8923 20.6793 13.617 23.436C13.2261 23.7638 12.6825 23.7638 12.2916 23.436C9.01714 20.6813 5 14.7536 5 10.1343C5 5.01135 8.40352 1 12.9543 1Z" fill="${colors.fill}"/>
                            <path d="M13.9383 23.8191L13.9389 23.8185C15.626 22.3986 17.484 20.1822 18.922 17.7371C20.3555 15.2996 21.4086 12.5723 21.4086 10.1343C21.4086 4.80632 17.8473 0.5 12.9543 0.5C8.06127 0.5 4.5 4.80632 4.5 10.1343C4.5 12.5723 5.55328 15.3002 6.98688 17.7378C8.42498 20.1832 10.283 22.3996 11.9697 23.8186L11.9703 23.8191C12.547 24.3027 13.3615 24.3027 13.9383 23.8191Z" stroke="${colors.stroke}"/>
                        </g>
                        <defs>
                            <filter id="filter0_d_306_3188" x="0" y="0" width="25.9086" height="32.6819" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                <feOffset dy="4"/>
                                <feGaussianBlur stdDeviation="2"/>
                                <feComposite in2="hardAlpha" operator="out"/>
                                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_306_3188"/>
                                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_306_3188" result="shape"/>
                            </filter>
                        </defs>
                    </svg>
                    <i class="fas fa-map-marker-alt marker-icon" style="color: ${colors.stroke};"></i>
                </div>
                ${
                    this.showMarkerText
                        ? `
                <div class="pin-text">
                    <div class="category-text" style="color: #000000; font-size: 14px;">${label.typeName}</div>
                    <div class="id-text" style="color: #000000; font-size: 13px;">${label.permitId}</div>
                </div>
                `
                        : ''
                }
            </div>
        </div>
    `;

        return L.divIcon({
            html: markerHtml,
            className: 'custom-div-icon',
            iconSize: [250, 60],
            iconAnchor: [20, 50],
        });
    }

    private setupSearchListener(): void {
        this.searchControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
            if (value) {
                this.filters.search = value;
            } else {
                delete this.filters.search;
            }
            this.pageIndex = 0;
            this.loadPermits();
        });
    }

    private setupAutoRefresh(): void {
        interval(this.REFRESH_INTERVAL_MS)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (!this.isDetailOpen()) {
                    this.loadPermits();
                }
            });
    }
}
